/**
 * @fileoverview Number widget — typed numeric input with min/max/step.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render <input type="number"> with min/max/step support.
 * - Coerce empty string to null; coerce text to number on commit.
 *
 * @component NumberField
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';

export function NumberField(props: BaseWidgetProps<number | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { min?: number; max?: number; step?: number; placeholder?: string };
  const aboveMax = extras.max != null && value != null && value > extras.max;
  const belowMin = extras.min != null && value != null && value < extras.min;
  const computedError = errorMessage
    ?? (aboveMax ? `Max ${extras.max}` : undefined)
    ?? (belowMin ? `Min ${extras.min}` : undefined);
  const isInvalid = !!computedError || (!!requiredUnmet && !!props.requiredImmediate);

  function commit(text: string) {
    if (text === '') return onChange(null);
    const n = Number(text);
    if (Number.isNaN(n)) return; // ignore non-numeric typing
    // Reject values outside the declared range — the host can still write
    // them programmatically, but the widget's onChange refuses out-of-range.
    if (extras.min != null && n < extras.min) return;
    if (extras.max != null && n > extras.max) return;
    onChange(n);
  }

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="number"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <input
        id={field.id}
        type="number"
        value={value ?? ''}
        onChange={(e) => commit(e.target.value)}
        min={extras.min}
        max={extras.max}
        step={extras.step}
        placeholder={extras.placeholder}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={isInvalid}
        className={`w-full rounded-xl px-3 py-2 text-sm outline-hidden focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 disabled:opacity-50 ${isInvalid ? 'border border-red-500 bg-red-50/50 text-red-900 ring-2 ring-red-500/20' : 'border border-slate-300 bg-white text-slate-900'}`}
      />
    </FieldWrapper>
  );
}
